import { useEffect, useMemo, useState } from 'react';

import { mediaQuerySizes, SimpleSlider, Typography } from '@capsule/consumer-design-system';

import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import FixedFloat from 'src/app/components/Elements/Page/Standard/FixedFloat';
import { SearchBar } from 'src/app/components/Elements/SearchBar';
import Spinner from 'src/app/components/Elements/Spinner';
import { StyledLink } from 'src/app/components/Elements/StyledLink';
import { ViewOrderToast } from 'src/app/components/Elements/ToastBanner/presets/ViewOrder';
import { OTCChatCard } from 'src/app/components/Models/OTC/OTCChatCard';
import { checkoutAddonSearch } from 'src/app/components/Pages/Essentials/CheckoutAddons/checkoutAddonSearch';
import { getCheckoutPath } from 'src/app/components/Pages/Essentials/CheckoutAddons/utils';
import { EssentialsItemCard } from 'src/app/components/Pages/Essentials/EssentialsItemCard/EssentialsItemCard';
import { useFacilityByAccountId } from 'src/app/hooks/data/facility';
import { useOTCMedCategoriesV2 } from 'src/app/hooks/data/otc';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import useExperiments from 'src/app/hooks/useExperiments';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { shop2Category } from 'src/app/router/paths';
import { getCartItemQuantity } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';
import type { OTCCategoryV2, OTCItemV2 } from 'src/app/types';

import { essentialsMoneyFormatter } from './utils';

const Page = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme }) => theme.colors.capsuleBlue50};
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${({ theme }) => theme.mediaQueries.medium} {
    background-color: ${({ theme }) => theme.colors.capsuleGreen00};
    margin: 0;
  }
  /*
  padding: ${({ theme }) => theme.space.s7};
  ${({ theme }) => theme.mediaQueries.mediumDown} {
    padding: ${({ theme }) => `${theme.space.s7} 0 ${theme.space.s7} ${theme.space.s7}`};
  }
  */
`;

const SearchResultsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 230px));
  grid-gap: 5px;
  margin: 0 32px;
  ${({ theme }) => theme.mediaQueries.mediumDown} {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }
`;

const CarouselContainer = styled.div`
  margin: 12px 0 24px 0;
  ${({ theme }) => theme.mediaQueries.medium} {
    padding: 0 32px;
  }
`;

const CardListItem = styled.li<{ hasCarouselSlides?: boolean }>``;

const StyledSearchBar = styled(SearchBar)`
  position: relative;
  width: 100%;
  ${({ theme }) => theme.mediaQueries.medium} {
    width: calc(100% - (${({ theme }) => theme.space.s6} * 2));
  }
`;

const MobileOnlyFullWidthGreenLine = styled.div.attrs({ 'aria-hidden': true })`
  ${({ theme }) => theme.mediaQueries.medium} {
    display: none;
  }

  height: 0.8rem;
  width: 100vw;
  background-color: ${({ theme }) => theme.colors.capsuleGreen00};
  position: relative;
`;

const CategoryNameArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M7.74407 16.4219C7.41863 16.7474 6.89099 16.7474 6.56556 16.4219C6.24012 16.0965 6.24012 15.5689 6.56556 15.2434L11.8096 9.99935L6.56556 4.75527C6.24012 4.42983 6.24012 3.9022 6.56556 3.57676C6.89099 3.25132 7.41863 3.25132 7.74407 3.57676L14.1667 9.99935L7.74407 16.4219Z"
      fill="#0F3C6C"
    />
  </svg>
);

export const Essentials = () => {
  useSegmentEvent('viewEssentials', { shouldTrackOnMount: true });
  const trackSearchEvent = useSegmentEvent('search', { shouldTrackOnMount: false });
  const trackAddToCartEvent = useSegmentEvent('addToCart', { shouldTrackOnMount: false });
  const trackRemoveFromCartEvent = useSegmentEvent('removeFromCart', { shouldTrackOnMount: false });
  const trackBeginCheckoutEvent = useSegmentEvent('beginCheckout', { shouldTrackOnMount: false });
  const trackSelectEssentialsCategoryEvent = useSegmentEvent('selectEssentialsCategory', { shouldTrackOnMount: false });

  const [isUpsellEssentialsEnabled, setUpsellEssentialsEnabled] = useState(false);

  const { data: allOtcCategories, isLoading } = useOTCMedCategoriesV2();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));

  const { fetchVariant } = useExperiments();
  const { data: facility, isSuccess: hasFacilityLoaded } = useFacilityByAccountId(user ? parseInt(user.accountId) : 0);

  useEffect(() => {
    if (!!user && hasFacilityLoaded) {
      const fetchUpsellEssentialsExperiment = async () => {
        const variant = await fetchVariant({
          experimentKey: 'upsell_essentials_12_21_23',
          userId: user.accountId,
          audienceAttributes: {
            facility_code: facility?.code,
          },
        });
        setUpsellEssentialsEnabled(variant === 'on');
      };
      fetchUpsellEssentialsExperiment();
    }
  }, [user, fetchVariant, facility, hasFacilityLoaded]);

  const cartItemQuantity = useSelector(getCartItemQuantity);

  const [isSearching, setIsSearching] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState<OTCCategoryV2[]>([]);

  const renderedCategories = isSearching ? filteredCategories : allOtcCategories;
  const flatSearchedItems = useMemo(() => {
    return filteredCategories
      .map(category => category.items)
      .flat()
      .reduce<OTCItemV2[]>((acc, obj) => {
        if (!acc.some(item => item.id === obj.id)) {
          acc.push(obj);
        }
        return acc;
      }, []);
  }, [filteredCategories]);

  return (
    <Page>
      {isLoading || !allOtcCategories ? (
        <Spinner centered />
      ) : (
        <section
          css={css`
            position: relative;
            padding-bottom: ${({ theme }) => theme.space.s8};
            width: 100%;
            max-width: var(--upsellMaxWidth);
          `}
        >
          <div
            css={css`
              padding: 32px;
            `}
          >
            <StyledSearchBar
              handleChange={e => {
                const searchText = e.currentTarget.value;
                setIsSearching(!!searchText);
                checkoutAddonSearch({ searchText, allOtcCategories, filteredCategories, setFilteredCategories });
              }}
              handleBlur={e => {
                const search_term = e.currentTarget.value;
                trackSearchEvent({ search_term, page: 'essentials' });
              }}
            />
          </div>

          {isSearching && flatSearchedItems.length === 0 && <OTCChatCard />}

          {isSearching && (
            <SearchResultsContainer>
              {flatSearchedItems.map((item, itemIndex: number) => {
                const formattedPrice = essentialsMoneyFormatter.format(item.price);
                const analyticsCartManipulationPayload = {
                  display_name_primary: item.name,
                  dosage: item.dosage,
                  price: formattedPrice,
                  quantity: item.quantity ?? 1,
                  OTC: true,
                  patientmedication_id: null,
                  prescription_fulfillment_id: null,
                  page: 'essentials_product_listing',
                };

                const position = [1, itemIndex + 1];
                return (
                  <EssentialsItemCard
                    minWidth={130}
                    {...item}
                    // Do not use item.id for key, because the same item can appear in multiple categories
                    // and having item cards with duplicate key will cause the react to not re-render.
                    key={`${item.id}_${itemIndex}`}
                    formattedPrice={formattedPrice}
                    onAddToCart={() => trackAddToCartEvent({ ...analyticsCartManipulationPayload, position })}
                    onRemoveFromCart={() => trackRemoveFromCartEvent(analyticsCartManipulationPayload)}
                  />
                );
              })}
            </SearchResultsContainer>
          )}

          {!isSearching && renderedCategories && renderedCategories?.length > 0 && (
            <ol
              css={css`
                list-style-type: none;
              `}
            >
              {renderedCategories.map((category, categoryIndex) => {
                return (
                  <CardListItem key={category.name} hasCarouselSlides={category.items.length > 5}>
                    <div
                      css={css`
                        margin-left: 32px;
                        display: flex;
                        height: 28px;
                        align-items: center;
                        a:hover,
                        a:visited,
                        a:focus,
                        a:active {
                          text-decoration: none; /* Ensures no underline on hover or other states */
                          color: inherit; /* Prevents color change in any state */
                        }
                      `}
                    >
                      <Link
                        css={css`
                          display: flex;
                          align-items: center;
                          justify-content: center;
                        `}
                        to={`${shop2Category.toPath()}?category=${encodeURIComponent(category.name)}`}
                        onClick={() => trackSelectEssentialsCategoryEvent({ source: 'caret', aisle: category.name })}
                      >
                        <Typography
                          css={css`
                            white-space: nowrap;
                          `}
                          as="span"
                          variant="heading3"
                        >
                          {category.name}
                        </Typography>
                        <CategoryNameArrow />
                      </Link>
                    </div>
                    {category.items.length > 0 && (
                      <CarouselContainer>
                        <SimpleSlider
                          key={`slider-${categoryIndex}`}
                          slidesPerView={3.3}
                          spaceBetweenSlides={20}
                          navigationButtonVariation={'circle'}
                          slides={category.items.map((item, itemIndex) => {
                            const formattedPrice = essentialsMoneyFormatter.format(item.price);

                            const analyticsCartManipulationPayload = {
                              display_name_primary: item.name,
                              dosage: item.dosage,
                              price: formattedPrice,
                              quantity: item.quantity ?? 1,
                              OTC: true,
                              patientmedication_id: null,
                              prescription_fulfillment_id: null,
                              page: 'essentials_main',
                            };

                            const position = [categoryIndex + 1, itemIndex + 1];

                            return (
                              <EssentialsItemCard
                                key={`${categoryIndex}-${itemIndex}`}
                                {...item}
                                formattedPrice={formattedPrice}
                                onAddToCart={() =>
                                  trackAddToCartEvent({ ...analyticsCartManipulationPayload, position })
                                }
                                onRemoveFromCart={() => trackRemoveFromCartEvent(analyticsCartManipulationPayload)}
                              />
                            );
                          })}
                          isPaginationCircleVisible={true}
                          isNavigationButtonVisible={true}
                          breakpoints={{
                            1: {
                              isLooped: false,
                              slidesPerView: 2.4,
                              slidesPerGroup: 1,
                              spaceBetweenSlides: 0,
                              isPaginationCircleVisible: false,
                              isNavigationButtonVisible: false,
                              slidesOffsetBefore: 24,
                              slidesOffsetAfter: 24,
                            },
                            440: {
                              isLooped: false,
                              slidesPerView: 3.4,
                              slidesPerGroup: 1,
                              spaceBetweenSlides: 0,
                              isPaginationCircleVisible: false,
                              isNavigationButtonVisible: false,
                              slidesOffsetBefore: 24,
                              slidesOffsetAfter: 24,
                            },
                            [mediaQuerySizes.medium]: {
                              isLooped: category.items.length / 2 > 1,
                              slidesPerView: 2,
                              slidesPerGroup: 2,
                              spaceBetweenSlides: 20,
                              isPaginationCircleVisible: true,
                              isNavigationButtonVisible: true,
                              slidesOffsetBefore: 0,
                              slidesOffsetAfter: 0,
                            },
                            980: {
                              isLooped: category.items.length / 3 > 1,
                              slidesPerView: 3,
                              slidesPerGroup: 3,
                              spaceBetweenSlides: 20,
                              isPaginationCircleVisible: true,
                              isNavigationButtonVisible: true,
                              slidesOffsetBefore: 0,
                              slidesOffsetAfter: 0,
                            },
                            1100: {
                              isLooped: category.items.length / 4 > 1,
                              slidesPerView: 4,
                              slidesPerGroup: 4,
                              spaceBetweenSlides: 20,
                              isPaginationCircleVisible: true,
                              isNavigationButtonVisible: true,
                              slidesOffsetBefore: 0,
                              slidesOffsetAfter: 0,
                            },
                            1320: {
                              isLooped: category.items.length / 5 > 1,
                              slidesPerView: 5,
                              slidesPerGroup: 5,
                              spaceBetweenSlides: 20,
                              isPaginationCircleVisible: true,
                              isNavigationButtonVisible: true,
                              slidesOffsetBefore: 0,
                              slidesOffsetAfter: 0,
                            },
                            1540: {
                              isLooped: category.items.length / 6 > 1,
                              slidesPerView: 6,
                              slidesPerGroup: 6,
                              spaceBetweenSlides: 20,
                              isPaginationCircleVisible: true,
                              isNavigationButtonVisible: true,
                              slidesOffsetBefore: 0,
                              slidesOffsetAfter: 0,
                            },
                          }}
                        />
                      </CarouselContainer>
                    )}

                    {categoryIndex !== renderedCategories.length - 1 && <MobileOnlyFullWidthGreenLine />}
                  </CardListItem>
                );
              })}
            </ol>
          )}

          {cartItemQuantity > 0 && (
            <FixedFloat
              css={css`
                z-index: 2;
              `}
            >
              <StyledLink
                variant={'noTextDecoration'}
                aria-label="View Order"
                onClick={() => trackBeginCheckoutEvent()}
                to={getCheckoutPath({ isUpsellEssentialsEnabled: isUpsellEssentialsEnabled })}
              >
                <ViewOrderToast cartItemCount={cartItemQuantity} />
              </StyledLink>
            </FixedFloat>
          )}
        </section>
      )}
    </Page>
  );
};
